"use client";

import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import { Icon } from "@to/ui";

import type { OpenHouseMapItem } from "@/lib/data/open-houses";
import type { OpenHouseViewSurface } from "@/lib/open-house-view";

/**
 * Task 34.9 — `ssr:false` boundary for the Leaflet open-house map. Leaflet reads
 * `window` at import time, so the real `<OpenHouseMap>` must NOT render on the
 * server. The homepage also waits until the map is near the viewport before
 * mounting this dynamic component, so its bundle and tiles do not compete with
 * the hero. `ssr:false` alone only defers the map until client rendering.
 */

function MapLoading() {
  return (
    <div
      className="flex h-full w-full items-center justify-center gap-2"
      style={{ backgroundColor: "var(--brand-surface-subtle)" }}
    >
      <Icon
        name="map"
        size={20}
        style={{ color: "var(--brand-text)" }}
      />
      <span style={{ color: "var(--brand-text)", fontSize: "13px" }}>
        Loading map…
      </span>
    </div>
  );
}

const OpenHouseMap = dynamic(() => import("./open-house-map"), {
  ssr: false,
  loading: MapLoading,
});

export function OpenHouseMapDynamic({
  items,
  basePath = "",
  deferUntilVisible = false,
  orgIdentifier,
  surface,
}: {
  items: OpenHouseMapItem[];
  /** Dev path-routing prefix (`/{orgSlug}`); empty in subdomain prod. Threaded
   *  to the map so popup links (`/agents/...`, `/properties/...`) resolve in dev. */
  basePath?: string;
  /** Homepage-only deferral; dedicated open-house pages mount immediately. */
  deferUntilVisible?: boolean;
  orgIdentifier?: string;
  surface?: OpenHouseViewSurface;
}) {
  const [nearViewport, setNearViewport] = useState(false);

  const containerRef = useCallback((container: HTMLDivElement | null) => {
    if (!container || !deferUntilVisible || nearViewport) return;

    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [deferUntilVisible, nearViewport]);

  return (
    <div ref={containerRef} className="h-full w-full">
      {!deferUntilVisible || nearViewport ? (
        <OpenHouseMap items={items} basePath={basePath} orgIdentifier={orgIdentifier} surface={surface} />
      ) : (
        <MapLoading />
      )}
    </div>
  );
}
